import { Link } from "react-router-dom";
import HeroCarousel from "./components/HeroCarousel";
import RecommendSection from "./components/RecommendSection";
import CategoryCard from "@/components/base/CategoryCard";
import SearchBar from "@/components/feature/SearchBar";
import Icon from "@/components/base/Icon";
import { categories } from "@/mocks/categories";
import {
  allComics,
  getComicsByCategory,
  getCompletedComics,
  getHotComics,
  getNewComics,
} from "@/utils/comic";
import { useSeo } from "@/hooks/useSeo";

const HOT_KEYWORDS = ["恋爱", "热血", "校园", "奇幻", "搞笑", "悬疑"];

export default function Home() {
  useSeo({
    title: "羞羞色漫-精品漫画在线阅读平台",
    description:
      "羞羞色漫网站为您提供丰富多样的漫画资源，包括最新连载和热门漫画，支持多设备阅读体验，满足漫画爱好者的阅读需求。",
    keywords: "羞羞色漫,漫画阅读,在线漫画,羞羞色漫画,好看的漫画,连载漫画,漫画平台",
  });

  return (
    <div className="w-full">
      <HeroCarousel />

      <section className="w-full px-4 md:px-8 -mt-8 relative z-10">
        <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
          <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-5 items-center">
            <div>
              <h2 className="font-heading text-lg font-bold text-foreground-950">
                发现你的下一部心动漫画
              </h2>
              <p className="mt-1 text-sm text-foreground-500">
                输入漫画名、作者或标签，快速找到想看的作品
              </p>
              <div className="mt-3">
                <SearchBar />
              </div>
            </div>
            <div className="flex flex-wrap gap-2 lg:justify-end">
              <span className="text-sm text-foreground-500 py-1">热门搜索：</span>
              {HOT_KEYWORDS.map((keyword) => (
                <Link
                  key={keyword}
                  to={`/search?q=${encodeURIComponent(keyword)}`}
                  className="px-3 py-1 rounded-full bg-background-100 text-sm text-foreground-700 cursor-pointer transition-colors hover:bg-primary-100 hover:text-primary-700"
                >
                  {keyword}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-10">
        <div className="flex items-center gap-3 mb-6">
          <span className="w-10 h-10 rounded-lg bg-primary-100 text-primary-600 flex items-center justify-center">
            <Icon name="ri-apps-2-line" className="w-5 h-5" />
          </span>
          <div>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
              分类快捷入口
            </h2>
            <p className="mt-1 text-xs md:text-sm text-foreground-500">
              按你喜欢的口味，快速进入对应漫画分类
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {categories.map((category) => (
            <CategoryCard
              key={category.id}
              category={category}
              count={getComicsByCategory(category.id).length}
            />
          ))}
        </div>
      </section>

      <RecommendSection
        title="最新上架"
        subtitle="近期新作与刚更新的连载，抢先看第一话"
        icon="ri-rocket-2-line"
        comics={getNewComics(8)}
        moreTo="/category/all"
      />

      <RecommendSection
        title="热门连载"
        subtitle="人气持续攀升的连载作品，追更不停"
        icon="ri-fire-line"
        comics={getHotComics(8)}
        moreTo="/category/all"
      />

      <RecommendSection
        title="完结精品"
        subtitle="一口气读完的完结佳作，不用等更新"
        icon="ri-award-line"
        comics={getCompletedComics(8)}
        moreTo="/search?status=completed"
      />

      <section className="w-full px-4 md:px-8 pb-4">
        <div className="rounded-lg border border-background-200 bg-secondary-50 p-6 md:p-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-medium">
                <Icon name="ri-information-line" className="w-3.5 h-3.5" />
                关于羞羞色漫
              </span>
              <h2 className="mt-4 font-heading text-2xl md:text-3xl font-bold text-foreground-950 leading-snug">
                为漫画爱好者打造的精品在线阅读平台
              </h2>
              <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed">
                我们专注漫画内容发现与阅读体验：清晰分类、章节预览、连载更新与个性化推荐，
                让你在任意设备上都能顺畅地找到并读完喜欢的作品。
              </p>
              <Link
                to="/about"
                className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors hover:bg-primary-600"
              >
                了解更多
                <Icon name="ri-arrow-right-line" className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: "ri-book-2-line", label: "在库作品", value: `${allComics.length}+` },
                { icon: "ri-grid-line", label: "漫画分类", value: `${categories.length}` },
                { icon: "ri-refresh-line", label: "每日更新", value: "持续" },
                { icon: "ri-device-line", label: "多端阅读", value: "全支持" },
              ].map((stat) => (
                <div
                  key={stat.label}
                  className="rounded-lg border border-background-200 bg-background-50 p-4"
                >
                  <span className="w-9 h-9 rounded-lg bg-primary-100 text-primary-600 flex items-center justify-center">
                    <Icon name={stat.icon} className="w-4 h-4" />
                  </span>
                  <p className="mt-3 font-heading text-xl font-bold text-foreground-950">
                    {stat.value}
                  </p>
                  <p className="text-xs text-foreground-500">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
        <p className="mt-4 text-center text-xs text-foreground-400">
          提示：本平台当前为演示站点，漫画数据与搜索、收藏、阅读设置均为前端模拟，后续可接入真实数据接口。
        </p>
      </section>
    </div>
  );
}